import React, { Context, createContext, useContext, useEffect, useReducer } from 'react';
import { useDidUpdateEffect } from '../../hooks/useDidUpdateEffect';
import { Asset } from '../../types/Asset';
import { FundsQueryContext } from './FundsQueryContext';

export type FundsSelectionContextProps = {
    funds: {
        selected: Asset[];
        selectedIds: string[];
        set: (fund: Asset[]) => void;
        add: (fund: Asset) => void;
        remove: (fund: Asset) => void;
        removeAll: () => void;
    };
    isSelected: (fund: Asset) => boolean;
    isSelectable: (fund: Asset) => boolean;
    selectionType: Asset['category'] | 'None';
};

export type FundsSelectionContextType = Context<FundsSelectionContextProps>;

export const createFundsSelectionContext = () => {
    return createContext<FundsSelectionContextProps>({
        funds: {
            selected: [],
            selectedIds: [],
            add: undefined!,
            remove: undefined!,
            removeAll: undefined!,
            set: undefined!,
        },
        isSelected: undefined!,
        isSelectable: undefined!,
        selectionType: 'None',
    });
};

export const FundsSelectionContext = createFundsSelectionContext();

export type FundsSelectionReducerAction =
    | { type: 'ADD'; payload: string }
    | { type: 'SET'; payload: string[] }
    | { type: 'REMOVE'; payload: string }
    | { type: 'REMOVE_ALL' };

export const fundsSelectionReducer: React.Reducer<{ ids: string[] }, FundsSelectionReducerAction> = (
    currentSelection: { ids: string[] },
    action: FundsSelectionReducerAction,
) => {
    switch (action.type) {
        case 'ADD':
            return { ids: [...currentSelection.ids.filter((id) => id !== action.payload), action.payload] };
        case 'SET':
            return { ids: [...action.payload] };
        case 'REMOVE':
            return { ids: [...currentSelection.ids.filter((id) => id !== action.payload)] };
        case 'REMOVE_ALL':
            return { ids: [] };
    }
};

export const WithFundsSelection: React.FC<{ context?: FundsSelectionContextType; syncToUrl?: boolean; children?: React.ReactNode }> = ({
    context = FundsSelectionContext,
    syncToUrl = false,
    children,
}) => {
    const fundsQueryContext = useContext(FundsQueryContext);

    const [selection, dispatch] = useReducer(fundsSelectionReducer, { ids: [] });

    const selected = fundsQueryContext.assets.all.filter((asset) => selection.ids.includes(asset.id + ''));

    const selectionKey = selected.reduce((total, asset) => {
        return total + asset.id + '-';
    }, '');

    const selectionType = selected.length > 0 ? selected[0]?.category : 'None';

    // Retreive selection from search params and prepopulate the selection
    useEffect(() => {
        const currentParams = new URLSearchParams(window.location.search);
        const fundIds = currentParams.getAll('fundId');
        dispatch({ type: 'SET', payload: fundIds });
    }, []);

    // Only triggers after load, skipping the first round.
    useDidUpdateEffect(() => {
        if (!syncToUrl) return;
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.delete('fundId');

        selected.forEach((asset) => {
            currentParams.append('fundId', asset.id);
        });

        const newUrl =
            window.location.protocol +
            '//' +
            window.location.host +
            window.location.pathname +
            '?' +
            currentParams.toString();

        window.history.pushState('', '', newUrl);
    }, [selectionKey]);

    return (
        <context.Provider
            value={{
                funds: {
                    selected,
                    selectedIds: selection.ids,
                    add: (fund) => {
                        dispatch({ type: 'ADD', payload: fund.id + '' });
                    },
                    remove: (fund) => {
                        dispatch({ type: 'REMOVE', payload: fund.id + '' });
                    },
                    removeAll: () => {
                        dispatch({ type: 'REMOVE_ALL' });
                    },
                    set: (funds) => {
                        dispatch({ type: 'SET', payload: funds.map((f) => f.id + '') });
                    },
                },
                isSelected: (fund) => {
                    return selection.ids.includes(fund.id + '');
                },
                isSelectable: (fund) => {
                    if (selected.length > 2) {
                        return false;
                    }
                    if (selected.length === 0) {
                        return true;
                    }
                    if (selected[0].category === fund.category) {
                        return true;
                    }
                    return false;
                },
                selectionType,
            }}
        >
            {children}
        </context.Provider>
    );
};
